import { useState, useEffect, useRef } from "react";
import { Button, Row, Col, Table, Form, Modal, Spinner } from "react-bootstrap";
import { UserInfo, StripePromise } from "../helper/Context";
import {
  canSetPersonalBranding,
  getVisualLifetimeHrs,
  getVisualLimit,
  getMonthlyPriceUSD,
  getAnnualPriceUSD,
  getMaxUploadSizeMB,
  canRestoreVisuals,
  canDownloadVisuals,
  getPresentationLimit,
} from "../helper/BusinessLogic";
import {
  Elements,
  PaymentElement,
  AddressElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import * as Yup from "yup";
import { Formik } from "formik";
import {
  contactUs,
  stripeElementsAppearance,
  shallowCopy,
} from "../helper/Utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX, faCheck } from "@fortawesome/free-solid-svg-icons";
import { APIRequestSubscriptionModification } from "../helper/APIFunctions";
import LoginModal from "../components/LoginModal";
import PaymentCompleteComponent from "../components/PaymentCompleteComponent";
import { useNavigate } from "react-router-dom";

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "long",
  day: "2-digit",
  hour: "numeric",
  minute: "2-digit",
});

const getVisCountText = (tier) => {
  const l = getVisualLimit(tier, false);
  return l ? `${l}` : "Unlimited";
};

const getVisTimeText = (tier) => {
  const l = getVisualLifetimeHrs(tier, false);
  return l ? `${l > 48 ? `${l / 24} days` : `${l} hours`}` : "Permanent";
};

const getPersonalBrandingText = (tier) => {
  const v = canSetPersonalBranding(tier);
  return (
    <FontAwesomeIcon
      size="2xl"
      className={"text-center " + (v ? "text-success" : "text-danger")}
      icon={v ? faCheck : faX}
      title={v ? "Can set personal branding" : "Cannot set personal branding"}
    />
  );
};

const getFileUploadSizeText = (tier) => {
  return `${getMaxUploadSizeMB(tier)} MB`;
};

const getRecoverText = (tier) => {
  const v = canRestoreVisuals(tier);
  return (
    <FontAwesomeIcon
      size="2xl"
      className={"text-center " + (v ? "text-success" : "text-danger")}
      icon={v ? faCheck : faX}
      title={
        v ? "Can restore deleted visuals" : "Cannot restore deleted visuals"
      }
    />
  );
};

const getDownloadText = (tier) => {
  const v = canDownloadVisuals(tier);
  return (
    <FontAwesomeIcon
      size="2xl"
      className={"text-center " + (v ? "text-success" : "text-danger")}
      icon={v ? faCheck : faX}
      title={
        v
          ? "Can download standalone visuals"
          : "Cannot download standalone visuals"
      }
    />
  );
};

const getPresentationText = (tier) => {
  const l = getPresentationLimit({ tier: tier }, false);
  return (
    <>
      <FontAwesomeIcon
        size="2xl"
        className={"text-center " + (l !== 0 ? "text-success" : "text-danger")}
        icon={l !== 0 ? faCheck : faX}
        title={l !== 0 ? "Can create slide decks" : "Cannot create slide decks"}
      />
      {l !== 0 ? (
        <div>{`(${l !== null ? l : "Unlimited"} deck${l != 1 ? "s" : ""
          })`}</div>
      ) : null}
    </>
  );
};

const getFreeButton = (userInfo, disabled, _billMonthly, onClickLogin, onClickFromOther, _onClickFromFree, className) => {
  if (!userInfo) {
    return <Button onClick={onClickLogin} className={className}>Register to get started!</Button>;
  }
  if (userInfo.tier == "free") {
    return <Button disabled className={className + " modern-button"}>Your current plan</Button>;
  }
  return (
    <Button onClick={onClickFromOther} disabled={disabled} className={className + " modern-button"}>
      Downgrade to free
    </Button>
  );
};
const getPremiumButton = (
  userInfo,
  disabled,
  billMonthly,
  onClickLogin,
  onClickFromOther,
  onClickFromFree,
  className
) => {
  if (!userInfo) {
    return <Button onClick={onClickLogin} className={className}>Register to try for free!</Button>;
  }
  const userBillMonthly = userInfo.billing_period == "month";
  if (userInfo.tier == "premium" && billMonthly == userBillMonthly) {
    return (
      <>
        {userInfo.free_trial_ends &&
          userInfo.free_trial_ends * 1000 > Date.now() ? (
          <div className="text-center text-warning fst-italic">
            Your free trial ends {dateFormatter.format(userInfo.free_trial_ends * 1000)}
          </div>
        ) : null}
        <Button disabled className={className}>Your current plan</Button>

      </>
    );
  }
  if (userInfo.tier == "free") {
    return (
      <Button onClick={onClickFromFree} disabled={!!disabled} className={className}>
        {userInfo.used_free_trial
          ? `Upgrade to premium (billed ${billMonthly ? "monthly" : "annually"
          })`
          : "Try for free for 14 days!"}
      </Button>
    );
  }
  return (
    <Button onClick={onClickFromOther} disabled={!!disabled} className={className}>
      Switch to to premium (billed {billMonthly ? "monthly" : "annually"})
    </Button>
  );
};

const getUnlimitedButton = (
  userInfo,
  disabled,
  billMonthly,
  onClickLogin,
  onClickFromOther,
  onClickFromFree,
  className
) => {
  if (!userInfo) {
    return <Button onClick={onClickLogin} className={className}>Register to try for free!</Button>;
  }
  const userBillMonthly = userInfo.billing_period == "month";
  if (userInfo.tier == "free") {
    return (
      <Button onClick={onClickFromFree} disabled={disabled} className={className}>
        {userInfo.used_free_trial
          ? `Upgrade to unlimited (billed ${billMonthly ? "monthly" : "annually"
          })`
          : "Try for free for 14 days!"}
      </Button>
    );
  }
  if (userInfo.tier == "unlimited" && billMonthly == userBillMonthly) {
    return (
      <>
        {userInfo.free_trial_ends &&
          userInfo.free_trial_ends * 1000 > Date.now() ? (
          <div className="text-center text-warning fst-italic">
            Your free trial ends {dateFormatter.format(userInfo.free_trial_ends * 1000)}
          </div>
        ) : null}
        <Button disabled className={className}>Your current plan</Button>
      </>
    );
  }
  return (
    <Button onClick={onClickFromOther} disabled={disabled} className={className}>
      Switch to unlimited (billed {billMonthly ? "monthly" : "annually"})
    </Button>
  );
};
const populateGoogleSubscriptionVerifyButton = () => {
  if (!document.getElementById("google-subscription-verify-button")) {
    setTimeout(populateGoogleSubscriptionVerifyButton, 50);
    return;
  }
  window?.google?.accounts?.id?.renderButton(
    document.getElementById("google-subscription-verify-button"),
    {
      theme: "filled_black", size: "large", text: "continue_with", shape: "pill", click_listener: () => {
        if (!window._snorkle) {
          window._snorkle = {};
        }
        window._snorkle.oauthRequestType = "subscription-modify";
      }
    }
  );
}
const ConfirmModal = (props) => {
  const [sendingCall, setSendingCall] = useState(false);
  const [result, setResult] = useState({ error: null, success: null });
  const [hideOauthVerify, setHideOauthVerify] = useState(false);
  const passSchema = Yup.object().shape({
    password: Yup.string()
      .required("Required")
      .min(8, "Too short")
      .max(72, "Too long"),
  });
  const get_tier_text = (tier) => {
    return tier == "unlimited" ? "an unlimited" : `a ${tier}`;
  };

  useEffect(() => {
    if (props.userInfo?.oauth_created && result.error && result.error.toString().toLowerCase().includes("identity") ) {
      populateGoogleSubscriptionVerifyButton();
    }
  }, [props.userInfo, result, hideOauthVerify]);

  useEffect(() => {
    document.getElementById("subscription-modify-confirm-modal").addEventListener("oauth-login-success", () => {
      setHideOauthVerify(true);
    });
    document.getElementById("subscription-modify-confirm-modal").addEventListener("oauth-login-error", () => {
      setHideOauthVerify(false);
      setResult({ error: "Unable to verify your identity. Please try again and contact support if the problem persists.", success: null });
    });
  }, []);
  const get_bp_text = (isMonthly) => {
    return isMonthly ? "monthly" : "annually";
  };
  const [callError, setCallError] = useState(null);
  useEffect(() => {
    if (result.success && result.success.timestamp && !result.success.isTrial) {
      const ui = shallowCopy(props.userInfo);
      ui.pending_change = {
        timestamp: result.success.timestamp,
        new_tier: props.newPlanTier,
        billing_period: props.newPlanIsMonthly ? "month" : "year",
      };
      props.setUserInfo(ui);
    }
    if (result.success && result.success.isTrial) {
      const ui = shallowCopy(props.userInfo);
      ui.tier = props.newPlanTier;
      ui.billing_period = props.newPlanIsMonthly ? "month" : "year";
      props.setUserInfo(ui);
    }
  }, [result]);
  const requiresPayment = () => {
    const sameTier = props.newPlanTier == props.oldPlanTier;
    const upgradeTier =
      (props.newPlanTier == "unlimited" && props.oldPlanTier == "premium") ||
      props.oldPlanTier == "free";
    const upgradePeriod = !props.newPlanIsMonthly && props.oldPlanIsMonthly;
    return (
      !props.newPlanTier != "free" &&
      (upgradeTier || (sameTier && upgradePeriod))
    );
  };
  const [show, setShow] = useState(true);
  const onHide = () => {
    setShow(false);
    setTimeout(props.onHide, 500);
  };
  const [forceOpen, setForceOpen] = useState(false);
  const currencyFormatter = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    currencyDisplay: "symbol",
  });
  return (
    <Modal onHide={() => (forceOpen ? null : onHide())} show={show} id="subscription-modify-confirm-modal">
      <Modal.Header closeButton closeVariant="white">
        <h3>Modify Current Subscription</h3>
      </Modal.Header>
      <Modal.Body>
        {result.error ? (
          <h5 className="text-center text-danger">{result.error}</h5>
        ) : null}
        {result.success ? (
          <>
            <h4 className="text-center text-success">Success</h4>
            <p>
              Your subscription plan has been updated. {result.success.isTrial ?
                <>{`Your free trial has been updated to a ${props.newPlanTier} plan. 
              After your tiral ends on ${dateFormatter.format(props.userInfo.free_trial_ends * 1000)}, you will be charged the full price of the ${props.newPlanTier} plan (${currencyFormatter.format(
                  props.newPlanIsMonthly ? getMonthlyPriceUSD(props.newPlanTier) : getAnnualPriceUSD(props.newPlanTier)
                )} ${props.newPlanIsMonthly ? "monthly" : "annually"}).`}</> : (<>{
                  result.success.timestamp && result.success.timestamp * 1000. > Date.now()
                    ? `Your new ${props.newPlanTier
                    } plan will take effect on ${dateFormatter.format(result.success.timestamp * 1000)}.`
                    : ` You have been ${result.success.total < 0 ? "credited" : "charged"
                    } ${currencyFormatter.format(
                      Math.abs((result.success.total || 0) / 100)
                    )} for activation of your new ${props.newPlanTier
                    } plan. Please refresh your page in a few minutes to see your new plan. If you do not see your new plan active after 15 minutes, please contact us.`}</>)}
            </p>
          </>
        ) : (
          <>
            <p>
              By proceeding you will be switching from{" "}
              {get_tier_text(props.oldPlanTier)} plan{" "}
              {props.oldPlanTier != "free" ? (
                <> billed {get_bp_text(props.oldPlanIsMonthly)}</>
              ) : null}{" "}
              to {get_tier_text(props.newPlanTier)} plan
              {props.newPlanTier != "free" ? (
                <> billed {get_bp_text(props.newPlanIsMonthly)}</>
              ) : null}
              .
            </p>
            {requiresPayment() && !props.oldPlanTier == "free" ? (
              props.oldPlanIsMonthly != props.newPlanIsMonthly ? (
                <p>
                  You will immediately be charged the difference between your
                  new plan's price and the prorated value of the remainder of
                  your current plan. Your subscription billing period will also
                  be changed and your next payment will be due 1{" "}
                  {props.newPlanIsMonthly ? "month" : "year"} from today.
                </p>
              ) : (
                <p>
                  You will immediately be charged the difference between your
                  the prorated value of the remainder of your current plan, and
                  your new plan for the same duration. Your subscription billing
                  period will remain unchanged, and you will pay the full price
                  for your new plan starting with your next billing cycle.
                </p>
              )
            ) : null}
            {requiresPayment() && props.oldPlanTier == "free" ? (
              <p>
                The payment method on file will be charged the full price of
                your new subscription,{" "}
                {new Intl.NumberFormat(undefined, {
                  style: "currency",
                  currency: "USD",
                  currencyDisplay: "symbol",
                }).format(
                  props.newPlanIsMonthly
                    ? getMonthlyPriceUSD(props.newPlanTier)
                    : getAnnualPriceUSD(props.newPlanTier)
                )}
                . This amount will be charged{" "}
                {get_bp_text(props.newPlanIsMonthly)} until your subscription is
                canceled or modified.
              </p>
            ) : null}
            {!requiresPayment() ? (
              <p>
                This change will take place at the end of your current billing
                period.
              </p>
            ) : null}
            {props.userInfo?.oauth_created && result.error && result.error.toString().toLowerCase().includes("identity") && !hideOauthVerify && (<>
              <p className="text-center text-weak">Please verify your identity to modify your subscription.</p>
              <div id="google-subscription-verify-button"></div>
            </>
            )}
            <Formik
              initialValues={{
                password: props.userInfo?.oauth_created ? "PLACEHOLDER" : "",
                comment: "",
                reason: "",
              }}
              validationSchema={passSchema}
              onSubmit={(values, actions) => {
                setSendingCall(true);
                setForceOpen(true);
                setResult({ error: null, success: null });
                APIRequestSubscriptionModification(
                  props.newPlanTier,
                  props.newPlanIsMonthly,
                  (result) => {
                    setForceOpen(false);
                    setSendingCall(false);
                    setResult({ error: null, success: result });
                  },
                  (error) => {
                    setForceOpen(false);
                    setSendingCall(false);
                    setHideOauthVerify(false);
                    setResult({ error: error, success: null });
                  },
                  values.password,
                  { cancelReason: values.reason, cancelComment: values.comment }
                ).then(actions.setSubmitting(false));
              }}
            >
              {({
                handleSubmit,
                handleChange,
                handleBlur,
                values,
                touched,
                errors,
                isSubmitting,
              }) => (
                <Form noValidate onSubmit={handleSubmit}>
                  <p className="text-danger text-center">{errors.global}</p>
                  {!props.userInfo?.oauth_created && (
                    <Form.Group controlId="formik-password" className="mb-3">
                      <Form.Label>Enter your password to confirm:</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        autoComplete="current-password"
                        value={values.password}
                        disabled={isSubmitting || sendingCall || result.success}
                        isInvalid={touched.password && errors.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.password}
                      </Form.Control.Feedback>
                    </Form.Group>
                  )}
                  {props.newPlanTier == "free" ? (
                    <>
                      <Form.Group controlId="formik-reason" className="mb-3">
                        <Form.Label>
                          Reason For Canceling (optional):
                        </Form.Label>
                        <Form.Select
                          name="reason"
                          value={values.reason}
                          disabled={
                            isSubmitting || sendingCall || result.success
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                        >
                          <option value="">Unspecified</option>
                          {cancelReasons.map((r) => {
                            return (
                              <option key={r[0]} value={r[0]}>
                                {r[1]}
                              </option>
                            );
                          })}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group controlId="formik-comment" className="mb-3">
                        <Form.Label>Feedback (optional):</Form.Label>
                        <Form.Control
                          as="textarea"
                          name="comment"
                          rows={3}
                          value={values.comment}
                          disabled={
                            isSubmitting || sendingCall || result.success
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Form.Group>
                    </>
                  ) : null}
                  <Button
                    type="submit"
                    className="mt-2"
                    disabled={isSubmitting || sendingCall || result.success}
                  >
                    {isSubmitting || sendingCall ? (
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="login"
                        aria-hidden="true"
                      />
                    ) : (
                      <>Confirm</>
                    )}
                  </Button>
                </Form>
              )}
            </Formik>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};
const cancelReasons = [
  ["too_expensive", "It's too expensive"],
  ["missing_features", "Some features are missing"],
  ["switched_service", "I'm switching to a different service"],
  ["unused", "I don't use the service enough"],
  ["customer_service", "Customer service was less than expected"],
  ["too_complex", "Ease of use was less than expected"],
  ["low_quality", "Quality was less than expected"],
  ["other", "Other reason"],
];

const StripeCheckout = (props) => {
  const [email, setEmail] = useState(
    props.paymentDetails.defaultEmail || props.userInfo.email
  );
  const validateEmail = () => {
    if (!email) {
      return "Your email is incomplete.";
    }
    if (!Yup.string().email().isValidSync(email)) {
      return "Your email is invalid.";
    } else {
      return null;
    }
  };
  const [resultPI, setResultPI] = useState(null);
  const [forceModal, setForceModal] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [emailError, setEmailError] = useState(null);
  const stripe = useStripe();
  const elements = useElements();

  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const emailError = validateEmail();
    if (emailError) {
      setEmailError(emailError);
      return;
    }
    if (!stripe || !elements) {
      return;
    }
    setSubmitting(true);
    let isPayment = !!props.paymentDetails.stripePaymentSecret;
    const { error, paymentIntent, setupIntent } = await (isPayment
      ? stripe.confirmPayment
      : stripe.confirmSetup)({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment/${props.paymentDetails.pendingTier}`,
          payment_method_data: {
            billing_details: {
              email: email,
            },
          },
        },
        redirect: "if_required",
      });
    setSubmitting(false);
    if (error) {
      setErrorMessage(error.message);
    } else {
      setResultPI(paymentIntent || setupIntent);
    }
  };
  return (
    <>
      {resultPI ? (
        <Modal
          show={true}
          onHide={() => {
            if (!forceModal) {
              props.onCheckout();
            }
          }}
        >
          <Modal.Header closeButton={!forceModal} closeVariant="white">
            {props.paymentDetails.stripePaymentSecret
              ? "Payment"
              : "Subscription"}{" "}
            Result
          </Modal.Header>
          <Modal.Body>
            <PaymentCompleteComponent
              paymentIntent={resultPI}
              newTier={props.paymentDetails.pendingTier}
              onTierUpdate={() => setForceModal(false)}
            />
          </Modal.Body>
        </Modal>
      ) : null}
      <form onSubmit={handleSubmit}>
        <Row>
          {props.paymentDetails.requireAddress ? (
            <Col xs={12} md={6}>
              <h5 className="TitleText">Billing Details</h5>
              <AddressElement
                options={{
                  mode: "billing",
                  defaultValues: {
                    name:
                      props.paymentDetails.defaultName || "",
                  },
                }}
              />
              <Form.Group style={{ paddingRight: "15px" }}>
                <Form.Label>Contact email</Form.Label>
                <Form.Control
                  placeholder="Email for payment confirmation"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setEmailError(null);
                  }}
                  onBlur={() => setEmailError(validateEmail())}
                  isInvalid={emailError}
                />
                <Form.Control.Feedback type="invalid">
                  {emailError}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          ) : null}
          <Col xs={12} md={props.paymentDetails.requireAddress ? 6 : 12}>
            <h5 className="TitleText">Payment Information</h5>
            <div className="mt-4">
              <PaymentElement options={{ mode: "setup" }} />
            </div>
          </Col>
        </Row>
        <div className="mt-3">
          <ul>
            {(props.paymentDetails.invoiceDescription || []).map((d, i) => {
              return (
                <li key={i}>
                  <Row>
                    <Col xs="auto">{d.description}</Col>
                    <Col xs="auto" className="ms-auto">
                      {new Intl.NumberFormat(undefined, {
                        style: "currency",
                        currency: d.currency.toUpperCase(),
                        currencyDisplay: "symbol",
                      }).format(d.price / 100)}
                    </Col>
                  </Row>
                </li>
              );
            })}
            {props.paymentDetails.fromBalance ? (
              <li>
                <Row>
                  <Col xs="auto">
                    Applied Account Credit (will be returned after 24 hours if
                    payment is not completed)
                  </Col>
                  <Col xs="auto" className="ms-auto">
                    {new Intl.NumberFormat(undefined, {
                      style: "currency",
                      currency: props.paymentDetails.currency.toUpperCase(),
                      currencyDisplay: "symbol",
                    }).format(props.paymentDetails.fromBalance / 100)}
                  </Col>
                </Row>
              </li>
            ) : null}
          </ul>
        </div>
        {errorMessage && <div className="text-danger">{errorMessage}</div>}
        <Button
          type="submit"
          disabled={
            !stripe || !elements || submitting || (resultPI && forceModal)
          }
          className="w-100"
        >
          {submitting ? (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="login"
              aria-hidden="true"
            />
          ) : (
            <>
              {props.paymentDetails.price ? (
                <>
                  Pay{" "}
                  {new Intl.NumberFormat(undefined, {
                    style: "currency",
                    currency: props.paymentDetails.currency,
                    currencyDisplay: "symbol",
                  }).format(props.paymentDetails.price / 100)}
                </>
              ) : null}
              {props.paymentDetails.nextChargeDays ? (
                <>
                  You will be charged{" "}
                  {new Intl.NumberFormat(undefined, {
                    style: "currency",
                    currency: props.paymentDetails.currency,
                    currencyDisplay: "symbol",
                  }).format(props.paymentDetails.pendingPrice / 100)}{" "}
                  when the {props.paymentDetails.nextChargeDays} day trial has
                  elapsed
                </>
              ) : null}
            </>
          )}
        </Button>
      </form>
    </>
  );
};

const PricingPage = (props) => {
  const starCanvasRef = useRef(null);

  const tiers = [
    {
      key: "free",
      name: "Free",
      description: "For exploring basic functionality",
      badge: null,
      getButton: getFreeButton,
    },
    {
      key: "premium",
      name: "Premium",
      description: "Optimized for content creation, professional communication, and exploring AI capabilities",
      badge: "Most Popular",
      getButton: getPremiumButton,
    },
    {
      key: "unlimited",
      name: "Unlimited",
      description: "Ideal for an independent researcher, scientist, or clinician. Expansive access to AI capabilities for analysis and video creation",
      badge: null,
      getButton: getUnlimitedButton,
    },
    {
      key: "group",
      name: "Group (multi-user)",
      description: "Perfect for a team, department, or practice",
      badge: null,
      getButton: null,
    },
  ];
  const getPriceText = (tier) => {
    const pm = getMonthlyPriceUSD(tier);
    const pa = getAnnualPriceUSD(tier);
    const currencyFormatter = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      currencyDisplay: "symbol",
    });
    return pm ? (
      billMonthly ? (
        <div style={{ color: '#f0f0f0' }}>
          {currencyFormatter.format(pm)} / month
          <span className="text-bold" style={{ fontSize: '0.95rem', color: '#cccccc', display: 'block', marginTop: 2 }}>
            <span style={{ fontSize: '0.85em' }}>
              (Save {new Intl.NumberFormat(undefined, { style: "percent" }).format(1 - pa / (pm * 12))} with annual billing)
            </span>
          </span>
        </div>
      ) : (
        <div style={{ color: '#f0f0f0' }}>
          {currencyFormatter.format(pa / 12)} / month
        </div>
      )
    ) : (
      <span style={{ color: '#f0f0f0' }}>Free</span>
    );
  };

  // Categorized features for cleaner display
  const coreFeatures = [
    "Interactive embeddable viewer",
    "PowerPoint add-in",
    "3D model tours",
    "Molecular visualizations",
    "DICOM medical imaging",
    "Share via QR/links/embeds",
    "Content search"
  ];

  const premiumFeatures = [
    "Slide deck platform",
    "Scrollytelling creator",
    "Permanent hosting",
    "Custom branding",
    "Restore deleted visuals",
    "DOI issuance (Crossref)",
    "Priority rendering"
  ];

  const aiFeatures = [
    "AI Video Generation (70+ languages)",
    "AI Organ Segmentation",
    "1-click 3D visualizations"
  ];

  const collaborationFeatures = [
    "Multi-user accounts",
    "Organization branding control",
    "Shared visual libraries",
    "Private/org content separation",
    "Dedicated support"
  ];

  // Helper to get features for each tier
  const getFeaturesForTier = (tierKey) => {
    const features = [];

    if (tierKey === 'free') {
      features.push({ header: "Core Features", items: [...coreFeatures, "Non-permanent hosting"], available: true });
      features.push({ header: "Premium Features", items: ["Premium features not included"], available: false });
      features.push({ header: "AI Features (alpha, request access)", items: ["AI features not included"], available: false });
    } else if (tierKey === 'premium') {
      features.push({
        header: "Core Features Included",
        items: [
          "All core features",
          "100MB single file size limit"
        ],
        available: true
      });
      features.push({ header: "Premium Features", items: premiumFeatures, available: true });
      features.push({ header: "AI Features (alpha, request access)", items: aiFeatures, available: true });
    } else if (tierKey === 'unlimited') {
      features.push({
        header: "Core Features Included",
        items: [
          "All core features",
          "250MB single file size limit"
        ],
        available: true
      });
      features.push({ header: "Premium Features", items: premiumFeatures, available: true });
      features.push({ header: "AI Features (alpha, request access)", items: aiFeatures, available: true });
    } else if (tierKey === 'group') {
      features.push({
        header: "Core & Premium Included",
        items: [
          "All core & premium features",
          "500MB single file size limit"
        ],
        available: true
      });
      features.push({ header: "AI Features (alpha, request access)", items: aiFeatures, available: true });
      features.push({ header: "Collaboration Features", items: collaborationFeatures, available: true });
    }

    return features;
  };

  const iter = [
    [
      "Create interactive visuals from any field of science and medicine, no app needed",
      (tier) => (
        <FontAwesomeIcon
          size="2xl"
          className="text-center text-success"
          icon={faCheck}
          title="Can create visuals"
        />
      ),
    ],
    [
      "Share interactive visuals and slide decks through QR, links and embed codes",
      (tier) => {
        return (
          <>
            {" "}
            <FontAwesomeIcon
              size="2xl"
              className="text-center text-success"
              icon={faCheck}
              title="Can share visuals"
            />
            {tier == "unlimited" ? (
              <div>Email sharing also included</div>
            ) : null}
          </>
        );
      },
    ],
    [
      "Search the universe and find 3D models and other interactive visuals (Beta)",
      () => (
        <FontAwesomeIcon
          size="2xl"
          className="text-center text-success"
          icon={faCheck}
          title="Search visuals"
        />
      ),
    ],
    [
      "Create interactive, 3D embeddable visuals of molecules and proteins",
      () => (
        <FontAwesomeIcon
          size="2xl"
          className="text-center text-success"
          icon={faCheck}
          title="Create molecule and protein visuals"
        />
      ),
    ],
    [
      "Permanently embed interactive visuals in your website",
      (tier) => (
        <FontAwesomeIcon
          size="2xl"
          className={
            "text-center " + (tier == "free" ? "text-danger" : "text-success")
          }
          icon={tier == "free" ? faX : faCheck}
          title={(tier != "free" ? "Can" : "Cannot") + " embed your visuals"}
        />
      ),
    ],
    [
      "Create interactive slide decks using Snorkle Slides (Beta)",
      getPresentationText,
    ],
    ["Use your own logo to brand visuals", getPersonalBrandingText],
    [
      "Access premium bundles of 3D visuals for clinical specialties",
      (tier) => (
        <FontAwesomeIcon
          size="2xl"
          className={
            "text-center " +
            (tier != "unlimited" ? "text-danger" : "text-success")
          }
          icon={tier != "unlimited" ? faX : faCheck}
          title={
            (tier == "unlimited" ? "Can" : "Cannot") + " embed your visuals"
          }
        />
      ),
    ],
    ["Duration visuals remain live", getVisTimeText],
    ["Number of visuals kept live ", getVisCountText],
    ["Max file upload size", getFileUploadSizeText],
    ["Enable offline viewing", getDownloadText],
    ["Restore deleted visuals", getRecoverText],
    ["Price", getPriceText],
  ];
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [apiResult, setApiResult] = useState({
    sending: false,
    result: null,
    error: null,
  });
  const onRequiresPaymentMethodError = (err) => {
    setPaymentDetails(null);
    setApiResult({
      sending: false,
      result: null,
      error: err.msg || err.toString() || "Internal Server Error",
    });
  };
  const onRequiresPaymentMethodSuccess = (msg) => {
    setPaymentDetails(msg);
    setApiResult({ sending: false, result: "Success", error: null });
  };

  const onRequiresPaymentMethod = (tier) => {
    setApiResult({ sending: true, result: null, error: null });
    APIRequestSubscriptionModification(
      tier,
      billMonthly,
      onRequiresPaymentMethodSuccess,
      onRequiresPaymentMethodError
    );
  };
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [billMonthly, setBillMonthly] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(null);
  let navigate = useNavigate();

  useEffect(() => {
    const canvas = starCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let stars = [];

    const handleResize = () => {
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      const intendedDrawBufferWidth = window.innerWidth;
      const intendedDrawBufferHeight = window.innerHeight;

      canvas.width = intendedDrawBufferWidth * dpr;
      canvas.height = intendedDrawBufferHeight * dpr;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);

      stars = [];
      for (let i = 0; i < 200; i++) {
        stars.push({
          x: Math.random() * intendedDrawBufferWidth,
          y: Math.random() * intendedDrawBufferHeight,
          r: Math.random() * 1.5,
          dx: (Math.random() - 0.5) * 0.3,
          dy: (Math.random() - 0.5) * 0.3
        });
      }
    };

    let animationFrameId;

    function draw() {
      if (!ctx || !canvas) return;
      ctx.save();

      const currentDrawWidth = window.innerWidth;
      const currentDrawHeight = window.innerHeight;

      ctx.clearRect(0, 0, currentDrawWidth, currentDrawHeight);

      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      stars.forEach(s => {
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, 2 * Math.PI);
        ctx.fill();
        s.x += s.dx;
        s.y += s.dy;
        if (s.x < 0) { s.x = currentDrawWidth + s.r; }
        else if (s.x > currentDrawWidth + s.r) { s.x = -s.r; }
        if (s.y < 0) { s.y = currentDrawHeight + s.r; }
        else if (s.y > currentDrawHeight + s.r) { s.y = -s.r; }
      });

      ctx.restore();
      animationFrameId = requestAnimationFrame(draw);
    }

    const initialResizeTimeoutId = setTimeout(() => {
      handleResize();
      draw();
    }, 0);

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(initialResizeTimeoutId);
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div
      className="pricing-page-container"
      style={{
        width: '100%',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(to bottom, #0a0f2c, #1b1f3a, #2d304d)',
      }}
    >
      <canvas
        id="stars"
        ref={starCanvasRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1
        }}
      ></canvas>
      <div
        className="overlay"
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'radial-gradient(circle at center, rgba(255,255,255,0.03), rgba(0,0,0,0.9))',
          pointerEvents: 'none',
          zIndex: 1
        }}
      ></div>
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          paddingTop: '5.5rem',
          paddingBottom: '4.5rem',
        }}
      >
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
        />
        <UserInfo.Consumer>
          {({ userInfo, setUserInfo }) => (
            <>
              {apiResult.sending && (
                <div className="loading-overlay">
                  <Spinner animation="border" role="status" className="loading-spinner-checkout">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              )}

              {showConfirmModal ? (
                <ConfirmModal
                  onHide={() => setShowConfirmModal(null)}
                  oldPlanIsMonthly={userInfo.billing_period == "month"}
                  oldPlanTier={userInfo.tier}
                  newPlanTier={showConfirmModal}
                  newPlanIsMonthly={billMonthly}
                  userInfo={userInfo}
                  setUserInfo={setUserInfo}
                />
              ) : null}
              <div style={{ padding: "48px 0 32px 0" }}>
                <h2 className="TitleText text-center mb-2" style={{ color: "white" }}>Pricing</h2>
                <p className="text-center mb-4" style={{ fontSize: "1.2rem", color: "#e0e0e0" }}>
                  Snorkle provides a variety of plans to meet your needs, including individual accounts and group plans.
                </p>
                <div className="d-flex justify-content-center mb-4">
                  <div className="billing-toggle">
                    <button
                      className={`billing-toggle-btn${!billMonthly ? " active" : ""}`}
                      onClick={() => setBillMonthly(false)}
                      type="button"
                    >
                      Annual
                    </button>
                    <button
                      className={`billing-toggle-btn${billMonthly ? " active" : ""}`}
                      onClick={() => setBillMonthly(true)}
                      type="button"
                    >
                      Monthly
                    </button>
                  </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <div className="pricing-cards-row" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'stretch', width: '100%' }}>
                    {tiers.map((tier, idx) => (
                      <div
                        key={tier.key}
                        className="plan-card"
                        style={{
                          boxShadow: '0 2px 12px 0 rgba(60, 60, 90, 0.09)',
                          borderRadius: 18,
                          border: '1.5px solid rgba(180, 180, 200, 0.18)',
                          background: 'rgba(255,255,255,0.15)',
                          minWidth: 260,
                          maxWidth: 320,
                          flex: '1 1 300px',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                          padding: '0 24px',
                          minHeight: 820,
                          marginBottom: 32,
                          paddingTop: 16
                        }}
                      >
                        {(tier.badge === "Most Popular" || (userInfo && userInfo.tier === tier.key && !tier.badge)) ? (
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: 8,
                              borderTopLeftRadius: 18,
                              borderTopRightRadius: 18,
                              background: tier.badge === "Most Popular" ? '#ffe066' : '#7ed957',
                              zIndex: 2
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: 8,
                              borderTopLeftRadius: 18,
                              borderTopRightRadius: 18,
                              background: '#506078',
                              zIndex: 2
                            }}
                          />
                        )}
                        <div className="plan-label" style={{ height: 24, textAlign: 'center', fontWeight: 700, color: tier.badge === "Most Popular" ? '#FFD600' : '#c0c0c0', letterSpacing: 1, marginTop: 8, marginBottom: 0, fontSize: 13 }}>
                          {(userInfo && userInfo.tier === tier.key ? 'YOUR CURRENT PLAN' : (tier.badge === "Most Popular" ? 'MOST POPULAR' : ''))}
                        </div>
                        <div className="plan-name" style={{ height: 32, textAlign: 'center', fontWeight: 700, fontSize: 22, marginTop: 8, marginBottom: 8, color: "white" }}>{tier.name}</div>
                        <div className="plan-desc" style={{ height: 90, textAlign: 'center', color: '#d0d0d0', fontSize: 14, margin: '0 16px 8px 16px', lineHeight: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {tier.description}
                        </div>
                        <div className="plan-price" style={{ height: 40, textAlign: 'center', fontWeight: 700, fontSize: 24, margin: '0 0 4px 0' }}>
                          {tier.key !== 'group' ? getPriceText(tier.key) : <span style={{ color: '#f0f0f0' }}>&nbsp;</span>}
                        </div>
                        <hr className="plan-divider" style={{ margin: '4px 0 8px 0', borderColor: 'rgba(255,255,255,0.2)' }} />
                        <div className="plan-features" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: 0, marginBottom: 8 }}>
                          {getFeaturesForTier(tier.key).map((section, sectionIdx) => (
                            <div key={sectionIdx} style={{ marginBottom: 12 }}>
                              <div style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: section.available ? '#fbbf24' : '#888',
                                marginBottom: 6,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5
                              }}>
                                {section.header}
                              </div>
                              {section.items.map((item, itemIdx) => {
                                // Check if it's a React element or has properties
                                const isReactElement = typeof item === 'object' && item !== null && item.$$typeof;
                                const hasProperties = typeof item === 'object' && item !== null && !item.$$typeof && item.text;

                                return (
                                  <div
                                    key={itemIdx}
                                    style={{
                                      fontSize: 14,
                                      margin: '0 0 4px 0',
                                      color: section.available ? '#e0e0e0' : '#777',
                                      textDecoration: section.available ? 'none' : 'line-through',
                                      paddingLeft: 12
                                    }}
                                  >
                                    • {isReactElement ? item : hasProperties ? (
                                      <>
                                        {item.text}
                                        {item.credits && (
                                          <> - <span style={{ color: '#fbbf24', whiteSpace: 'nowrap' }}>{item.credits}</span></>
                                        )}
                                        {item.detail && <> {item.detail}</>}
                                      </>
                                    ) : item}
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                        <div style={{ flex: 1 }}></div>
                        <hr className="plan-divider" style={{ margin: '8px 0', borderColor: 'rgba(255,255,255,0.2)' }} />
                        <div className="plan-action-row" style={{ width: '100%', textAlign: 'center', marginBottom: 24, display: 'flex', justifyContent: 'center' }}>
                          {tier.getButton ? (
                            <div style={{ width: '90%' }}>
                              {tier.getButton(
                                userInfo,
                                apiResult.sending || paymentDetails,
                                billMonthly,
                                () => setShowLoginModal(true),
                                () => setShowConfirmModal(tier.key),
                                () =>
                                  userInfo.has_default_payment_method
                                    ? setShowConfirmModal(tier.key)
                                    : onRequiresPaymentMethod(tier.key),
                                "plan-action-button modern-button"
                              )}
                            </div>
                          ) : (
                            <Button className="plan-action-button modern-button" style={{ width: '90%' }} onClick={() => navigate("/contact")}>Contact us</Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {paymentDetails && (
                <Modal
                  show={!!paymentDetails}
                  onHide={() => setPaymentDetails(null)}
                  centered
                  dialogClassName="modern-checkout-modal"
                  backdrop="static"
                >
                  <Modal.Header closeButton>
                    <Modal.Title>
                      {paymentDetails.stripePaymentSecret || paymentDetails.stripeSetupSecret
                        ? "Complete Your Purchase"
                        : "Account Credit Confirmation"}
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    {paymentDetails.stripePaymentSecret || paymentDetails.stripeSetupSecret ? (
                      <StripePromise.Consumer>
                        {(stripePromise) => (
                          <Elements
                            stripe={stripePromise}
                            options={{
                              clientSecret:
                                paymentDetails.stripePaymentSecret ||
                                paymentDetails.stripeSetupSecret,
                              appearance: stripeElementsAppearance,
                            }}
                          >
                            <StripeCheckout
                              userInfo={userInfo}
                              paymentDetails={paymentDetails}
                              onCheckout={() => {
                                setPaymentDetails(null);
                              }}
                            />
                          </Elements>
                        )}
                      </StripePromise.Consumer>
                    ) : (
                      <div className="text-center">
                        <h4 className="text-success">Success!</h4>
                        <p className="mt-3">
                          Your {paymentDetails.pendingTier} plan has been successfully activated using your account credit.
                        </p>
                        <p>
                          Your current account credit is {new Intl.NumberFormat(undefined, {
                            style: "currency",
                            currency: paymentDetails.currency.toUpperCase(),
                            currencyDisplay: "symbol",
                          }).format(-paymentDetails.endBalance / 100)}.
                        </p>
                        <Button variant="primary" className="mt-4 modern-button" onClick={() => setPaymentDetails(null)}>Close</Button>
                      </div>
                    )}
                  </Modal.Body>
                </Modal>
              )}

              {apiResult.error && !apiResult.sending && !paymentDetails && (
                <div className="text-center mt-4">
                  <h5 style={{ color: '#ff8a80' }}>Payment Error</h5>
                  <p style={{ color: '#ffcdd2' }}>{apiResult.error}</p>
                </div>
              )}
            </>
          )}
        </UserInfo.Consumer>
      </div>
    </div>
  );
};

export default PricingPage;
